import Konva from 'konva'
import { Reactive, reactive } from 'vue'
import { useLogsStore } from '@/store/logs'
import { CanvasPluginSettings } from '@/types/Canvas'
import { DateTime } from 'luxon'

export default abstract class CanvasPluginProvider<ConfigType extends { enabled: boolean }> {
  group: Konva.Group
  layer: Konva.Layer
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean }>
  protected needsToBeReRendered: boolean
  protected needsToBeReloaded: boolean
  protected fetchInformationAbortController: AbortController | null = null
  protected fetchInformationPromise: Promise<void> | null = null

  constructor(
    protected settings: CanvasPluginSettings,
    protected providerType: string,
    protected config: ConfigType,
    protected needsReloadOnTimestampChange: boolean = true,
    protected needsReRenderOnTimestampChange: boolean = true
  ) {
    this.providerType = providerType
    this.group = new Konva.Group()
    this.layer = new Konva.Layer()
    this.layer.add(this.group)
    this.needsReloadOnTimestampChange = needsReloadOnTimestampChange
    this.needsReRenderOnTimestampChange = needsReRenderOnTimestampChange
    this.reactiveProperties = reactive({
      loading: false,
      enabled: settings.enabled,
    })
    if (this.reactiveProperties.enabled) {
      this.layer.show()
    } else {
      this.layer.hide()
    }
    this.needsToBeReRendered = true
    this.needsToBeReloaded = true
    this.render()
  }

  abstract fetchInformation(): [Promise<void>, AbortController]

  abstract draw(): void

  render() {
    if (!this.needsToBeReRendered) return
    if (!this.isEnabled()) return
    if (this.reactiveProperties.loading) {
      if (this.fetchInformationAbortController) this.fetchInformationAbortController.abort()
      this.fetchInformationPromise = null
      this.fetchInformationAbortController = null
    }

    if (this.needsToBeReloaded) {
      this.needsToBeReRendered = false
      this.needsToBeReloaded = false
      this.reactiveProperties.loading = true
      const fetchInformationResult = this.fetchInformation()
      this.fetchInformationPromise = fetchInformationResult[0]
      this.fetchInformationAbortController = fetchInformationResult[1]
      this.fetchInformationPromise
        .then(() => {
          this.draw()
          this.render()
        })
        .catch(error => {
          useLogsStore().addLogEntry({
            message: 'Error while fetching Plugin information',
            tag: 'canvas',
            level: 'ERROR',
            error: error,
          })
        })
        .finally(() => {
          this.fetchInformationPromise = null
          this.fetchInformationAbortController = null
          this.reactiveProperties.loading = false
        })
    } else {
      this.needsToBeReRendered = false
      this.needsToBeReloaded = false
      this.draw()
    }
  }

  getLayer() {
    return this.layer
  }

  enable() {
    if (this.reactiveProperties.enabled) return
    this.reactiveProperties.enabled = true
    this.needsToBeReRendered = true
    this.needsToBeReloaded = true
    this.render()
    this.layer.show()
  }

  disable() {
    this.reactiveProperties.enabled = false
    this.layer.hide()
  }

  isEnabled() {
    return this.reactiveProperties.enabled
  }

  rotate(rotation: number) {
    this.group.rotation(rotation)
    this.layer.batchDraw()
  }

  updateTimestamp(timestamp: DateTime) {
    this.settings.timestamp = timestamp
    if (this.needsReRenderOnTimestampChange) this.needsToBeReRendered = true
    if (this.needsReloadOnTimestampChange) this.needsToBeReloaded = true
    this.render()
  }

  forceUpdate() {
    this.needsToBeReRendered = true
    this.needsToBeReloaded = true
    this.render()
  }

  abstract doesConfigChangeNeedRerender(config: ConfigType): boolean

  abstract doesConfigChangeNeedReload(config: ConfigType): boolean

  updateConfig(config: ConfigType) {
    this.needsToBeReloaded = this.doesConfigChangeNeedReload(config)
    this.needsToBeReRendered = this.doesConfigChangeNeedRerender(config) || this.needsToBeReloaded
    this.config = config
    if (this.needsToBeReRendered) {
      this.render()
    }
  }

  destroyPlugin() {
    this.group.destroy()
    this.layer.destroy()
  }
}
